import { t } from "@lingui/macro";
import {
  BuildingsIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ReadCvLogoIcon,
  UserCircleIcon,
  UsersIcon,
} from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from "@reactive-resume/ui";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router";

import {
  useAdminResumes,
  useAdminStats,
  useAdminUsers,
  useDepartments,
} from "@/client/services/admin";

import { CreateUserDialog } from "./_dialogs/create-user";

export const AdminPage = () => {
  const [userSearch, setUserSearch] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState<string>("all");
  const [selectedRole, setSelectedRole] = useState<string>("all");
  const [createUserOpen, setCreateUserOpen] = useState(false);

  const { stats, loading: statsLoading } = useAdminStats();
  const { users, loading: usersLoading } = useAdminUsers({
    search: userSearch || undefined,
    department: selectedDepartment === "all" ? undefined : selectedDepartment,
    role: selectedRole === "all" ? undefined : selectedRole,
    limit: 1000,
  });
  const { resumes, loading: resumesLoading } = useAdminResumes({ limit: 5 });
  const { departments } = useDepartments();

  return (
    <>
      <Helmet>
        <title>
          {t`Admin Dashboard`} - {t`Bloomteq Resume`}
        </title>
      </Helmet>

      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">{t`Admin Dashboard`}</h1>
          <p className="text-muted-foreground">
            {t`Manage users and resumes across your organization`}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t`Total Users`}</CardTitle>
              <UsersIcon className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {statsLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <div className="text-2xl font-bold">{stats?.totalUsers ?? 0}</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t`Total Resumes`}</CardTitle>
              <ReadCvLogoIcon className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {statsLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <div className="text-2xl font-bold">{stats?.totalResumes ?? 0}</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t`Departments`}</CardTitle>
              <BuildingsIcon className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {statsLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <div className="text-2xl font-bold">
                  {stats?.usersByDepartment?.length ?? 0}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t`Admins`}</CardTitle>
              <UserCircleIcon className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {statsLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <div className="text-2xl font-bold">
                  {stats?.usersByRole?.find((r) => r.role === "ADMIN")?.count ?? 0}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Users Section */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>{t`Users`}</CardTitle>
              <CardDescription>{t`View and manage all users in your organization`}</CardDescription>
            </div>
            <Button onClick={() => setCreateUserOpen(true)}>
              <PlusIcon className="mr-2 size-4" />
              {t`Create User`}
            </Button>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="mb-4 flex flex-wrap gap-4">
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t`Search users...`}
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={t`All Departments`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t`All Departments`}</SelectItem>
                  {departments
                    ?.filter((dept) => dept && dept.trim() !== "")
                    .map((dept) => (
                      <SelectItem key={dept} value={dept}>
                        {dept}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder={t`All Roles`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t`All Roles`}</SelectItem>
                  <SelectItem value="WORKER">{t`Employee`}</SelectItem>
                  <SelectItem value="MANAGER">{t`Manager`}</SelectItem>
                  <SelectItem value="ADMIN">{t`Admin`}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Users List */}
            <div className="space-y-2">
              {usersLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                    <Skeleton className="h-8 w-16" />
                  </div>
                ))
              ) : users?.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  {t`No users found`}
                </div>
              ) : (
                users?.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between rounded-lg border p-4"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{user.name}</span>
                        <Badge
                          variant={
                            user.role === "ADMIN"
                              ? "primary"
                              : user.role === "MANAGER"
                                ? "secondary"
                                : "info"
                          }
                        >
                          {user.role === "WORKER" ? t`Employee` : user.role === "MANAGER" ? t`Manager` : t`Admin`}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {user.email} {user.department && `· ${user.department}`} ·{" "}
                        {user._count?.resumes ?? 0} {t`resumes`}
                      </div>
                    </div>
                    <Button asChild variant="ghost" size="sm">
                      <Link to={`/dashboard/admin/users/${user.id}`}>{t`View`}</Link>
                    </Button>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Resumes Section */}
        <Card>
          <CardHeader>
            <CardTitle>{t`Recent Resumes`}</CardTitle>
            <CardDescription>{t`Latest resumes created across the organization`}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {resumesLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                    <Skeleton className="h-8 w-16" />
                  </div>
                ))
              ) : resumes?.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  {t`No resumes found`}
                </div>
              ) : (
                resumes?.map((resume) => (
                  <div
                    key={resume.id}
                    className="flex items-center justify-between rounded-lg border p-4"
                  >
                    <div className="space-y-1">
                      <div className="font-medium">{resume.title}</div>
                      <div className="text-sm text-muted-foreground">
                        {resume.user?.name} {resume.user?.department && `· ${resume.user.department}`}{" "}
                        · {new Date(resume.updatedAt).toLocaleDateString()}
                      </div>
                    </div>
                    <Button asChild variant="ghost" size="sm">
                      <Link to={`/builder/${resume.id}`}>{t`View`}</Link>
                    </Button>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <CreateUserDialog open={createUserOpen} onOpenChange={setCreateUserOpen} />
    </>
  );
};
